import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppLayout from "../components/AppLayout";
import ProductTable from "../components/productTable";
import ProductForm from "../components/ProductForm";
import Header from "../components/header";
import {
  addBusinessProduct,
  deleteBusinessProduct,
  editBusinessProduct,
  getBusinessProducts,
} from "../logic/business/action";
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { MdRefresh, MdAdd } from "react-icons/md";

const ProductsScreen = () => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.auth);
  const businessSelector = useSelector((state) => state.business);
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState("add");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editId, setEditId] = useState(0);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  useEffect(() => {
    if (selector?.user?.business_id) {
      getBusinessProducts(dispatch, selector?.user?.business_id);
    }
  }, [selector?.user?.business_id]);

  const handleFormSubmit = async (values) => {
    try {
      if (sidebarMode === "add") {
        await addBusinessProduct(
          dispatch,
          selector.user.business_id,
          values
        );
        setSidebarOpen(false);
      } else {
        await editBusinessProduct(dispatch, editId, values, selector?.user?.business_id);
        setSidebarOpen(false);
        getBusinessProducts(dispatch, selector?.user?.business_id);
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      throw error;
    }
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setSidebarMode("add");
    setEditId(0);
    setSidebarOpen(true);
  };

  const handleEditProduct = (productData) => {
    setSelectedProduct(productData);
    setEditId(productData?.product_id);
    setSidebarMode("edit");
    setSidebarOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteBusinessProduct(dispatch, productId, selector?.user?.business_id);
      setShowConfirmationPopup(false);
      getBusinessProducts(dispatch, selector?.user?.business_id);
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const refreshProducts = () => {
    getBusinessProducts(dispatch, selector?.user?.business_id);
  };

  return (
    <AppLayout>
      <ProductForm
        open={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setEditId(0);
          setSelectedProduct(null);
        }}
        mode={sidebarMode}
        productData={selectedProduct}
        onSubmit={handleFormSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showConfirmationPopup}
        onClose={() => setShowConfirmationPopup(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1f2937' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this product? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowConfirmationPopup(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteProduct(productToDelete);
            }}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        px: 3,
        pt: 1,
        pb: 3, 
        gap: 1 
      }}>
        <Header
          title={"Products"}
          middle={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={refreshProducts}
                sx={{ 
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  '&:hover': { 
                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                  }
                }}
              >
                <MdRefresh />
              </IconButton>
            </Box>
          }
          actions={
            <>
              <Button
                variant="contained"
                onClick={handleAddProduct}
                startIcon={<MdAdd />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                  },
                }}
              >
                Add Product
              </Button>
            </>
          }
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ProductTable
            setEditMode={(data) => {
              handleEditProduct(data);
            }}
            setAddProductMode={(data) => {
              handleEditProduct(data);
            }}
            onDeleteProduct={(productId) => {
              setProductToDelete(productId);
              setShowConfirmationPopup(true);
            }}
          />
        </Box>
      </Box>
    </AppLayout>
  );
};

export default ProductsScreen;

